import TextField from "@mui/material/TextField/TextField";
import React, { useState } from "react";
import Avatar from "@mui/material/Avatar/Avatar";
import { stringAvatar } from "../../../utils/avatar";
import styles from "./styles.module.scss";
import { SearchResourceBySkill } from "../../../@types/search-skills.type";
import InfoIcon from '@mui/icons-material/Info';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import iconManager from "../../../assets/icon-manager.svg";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Select from "@mui/material/Select/Select";
import MenuItem from "@mui/material/MenuItem/MenuItem";
import FormControl from "@mui/material/FormControl/FormControl";
import InputLabel from "@mui/material/InputLabel/InputLabel";
import Button from "@mui/material/Button/Button";
import SearchIcon from '@mui/icons-material/Search';
import { SkillDetailsModal } from "./components/SkillDetailsModal";
import Tooltip from "@mui/material/Tooltip/Tooltip";
import IconButton from "@mui/material/IconButton/IconButton";
import { searchResourcesByQuestion } from "../../../services/api/resource/resourceService";
import { ISearchResourcesByQuestion, ISearchResourcesByQuestionResponse } from "../../../@types/resource.type";
import { IApiResponse } from "../../../@types/api-response.type";
import ReactApexChart from "react-apexcharts";
import CircularProgress from "@mui/material/CircularProgress/CircularProgress";
import { toast } from "react-toastify";



const schema = z.object({
  description: z
    .string()
    .min(1, "O texto não pode ser nulo"),
    quantity: z
    .number()
});

export type resourcesSkillData = z.infer<typeof schema>;


export const SkillsResources: React.FC = ({

}) => {
  const [resourcesBySkills, setResourcesBySkills] = useState<SearchResourceBySkill[]>();
  const [resourceId, setResourceId] = useState<string>();
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);


  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<resourcesSkillData>({
    resolver: zodResolver(schema),
    defaultValues: {
      description: "",
      quantity: 5,
    },
  });

  const onSearch = async (data: resourcesSkillData) =>  {
    if(data){
      setLoading(true);
      const search = {
        question: data.description,
        quantity: data.quantity
      } as ISearchResourcesByQuestion;
      try {
        const response = await searchResourcesByQuestion(search) as IApiResponse<ISearchResourcesByQuestionResponse> | [];
        if(response && response.result?.items)
          setResourcesBySkills(response?.result.items);
        else
          toast.error("Não foi possível buscar os colaboradores. Entre em contato com o suporte");
      } catch (error) {
        console.log(error)
        toast.error("Não foi possível buscar os colaboradores. Entre em contato com o suporte");
        console.log("");
      } finally{
        setLoading(false);
      }
    }
  }

  function openSkillsModal(id: string){
    setOpenModal(true);
    setResourceId(id);
  }

  function addHTML(resource: SearchResourceBySkill[]){
    const html = [];
    for (let index = 0; index < resource.length; index++) {
      const element = resource[index];
      const options = {
        series: [Math.round(element.similarityPercentage)],
        options: {
          chart: {
            height: 100,
            type: 'radialBar',
          },
          plotOptions: {
            radialBar: {
              inverseOrder: false,
              startAngle: 0,
              endAngle: 360,
              offsetX: 0, // Move chart sides
              offsetY: 0, // Move chart top, bottom
              hollow: {
                size: '50%',
              },
              dataLabels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '13px',
                  fontFamily: undefined,
                  fontWeight: 'bold',
                  offsetY: 50,
                  colors: undefined
                },
                value: {
                  show: true,
                  fontSize: '13px',
                  fontFamily: undefined,
                  fontWeight: 600,
                  color: '#781AA0',
                  offsetY: -10,
                  formatter: function(val) {
                    return val + "%";
                  }
                }
              },
            },
          },
          colors: ["#781AA0"],
          labels: ['Compatibilidade'],
        },
      };
      html.push(
        <div className={styles.info} onClick={() => openSkillsModal(element.resourceId)}>
          <div className={styles.mainInfo}>
            <div className={styles.leftInfo}>
              <div id="chart">
                <ReactApexChart
                  options={options.options}
                  series={options.series}
                  type="radialBar"
                  height={100}
                  width={100}
                />
              </div>
              <div id="html-dist"></div>
            </div>
            <div>
              <Avatar className={styles.avatar}
                {...stringAvatar(element.resourceName)}
              />
            </div>
            <div className={styles.rightInfo}>
              <div>
                {element.resourceName}
              </div>
              <div>
                <Tooltip title={"Função"}>
                  <IconButton>
                    <AssignmentIndIcon style={{fill:"#212121"}} />
                  </IconButton>
                </Tooltip>
                {element.position}
              </div>
              <div>
                <Tooltip title={"Gerente"}>
                  <IconButton>
                    <img className={styles.iconManager} src={iconManager}></img>
                  </IconButton>
                </Tooltip>
                {element.managerName}
              </div>
            </div>
          </div>
          <div className={styles.skill}>
            {element.motivation}
          </div>
        </div>
      );
    }
    return html;
  }

  return (
    <div className={styles.skills}>
      <div className={styles.inputGroup}>
        <Tooltip title={<div>
          <div>
            Descreva com clareza o perfil de colaborador que você está buscando. 
            A partir da sua necessidade, vamos sugerir colaboradores que tenham maior 
            proximidade com as competências, experiências e certificações que você descreveu.
          </div>
  
          <div className={styles.tooltipTextBody}>
            <div>
              <b>
                ✅ Exemplos de como preencher corretamente:
              </b>
            </div>
            <div>
              <i>
                'Preciso de um colaborador com experiência em Python e que tenha atuado no setor de Óleo e Gás.'
              </i>
            </div>
            <div>
              <i>
                'Busco alguém com perfil de liderança, conhecimento em metodologias ágeis e experiência em gestão de equipes.'
              </i>
            </div>
            <div>
              <i>
                'Quero alguém com conhecimento em banco de dados PostgreSQL e experiência em projetos de BI.'
              </i>
            </div>
          </div>
          <div className={styles.tooltipTextBody}>
            <div>
              <b>
                ⚠️ Dicas para uma boa descrição:
              </b>
            </div>
            <div>
              Seja claro sobre as habilidades técnicas necessárias (ex: Java, Excel, AutoCAD).
            </div>
            <div>
              Mencione, se possível, o segmento ou tipo de projeto desejado (ex: saúde, indústria, tecnologia).
            </div>
            <div>
              Inclua soft skills, experiências específicas ou certificações que você considere 
              importantes (ex: trabalho em equipe, atuação em startups, experiência internacional, azure devops).
            </div>
            <div>
              Quanto mais precisa for a sua descrição, maior será a chance de receber sugestões alinhadas ao seu objetivo.
            </div>
          </div>
        </div>}>
          <IconButton>
            <InfoIcon />
          </IconButton>
        </Tooltip>
        <form onSubmit={handleSubmit(onSearch)}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                label="Escreva sua necessidade"
                placeholder={
                  "Colaboradores com habilidades em Backend C# e Frontend React"
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                error={!!errors.description}
                helperText={errors.description?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="quantity"
            control={control}
            defaultValue={5}
            render={({ field }) => (
              <FormControl style={{width: "100px"}} >
                <InputLabel id="resources-quantity">Qtd</InputLabel>
                <Select
                  {...field}
                  fullWidth
                  labelId="resources-quantity"
                  label="Tipo de Campo"
                  onChange={(e) => field.onChange(e.target.value)}
                  error={!!errors.quantity}
                >
                  <MenuItem value={3}>3</MenuItem>
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                </Select>
              </FormControl>
            )}
          />

          <Button
            variant="contained"
            color="primary"
            type="submit"
            // disabled={!isEnabled || loading}
            className={styles.btnsSave}
          >
            <SearchIcon />
          </Button>
        </form>
      </div>
      {loading ? (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <CircularProgress size={100} />
          </div>
        ) : (
        <div className={styles.cardBlock}>
          { resourcesBySkills && addHTML(resourcesBySkills)}
        </div>
      )}
      {
        openModal && resourceId &&
        <SkillDetailsModal openModal={openModal} setOpenModal={setOpenModal} resourceId={resourceId} />
      }
    </div>
  );
};
